import { z } from "zod";

import { http } from "@/apis";

import type {
  IDnsRecord,
  IDnsRecordListResult,
  ICloudflareZone,
} from "@/features/dns/schemas/dns";
import {
  cloudflareZoneSchema,
  dnsConfiguredZoneSchema,
  dnsRecordListResultSchema,
  dnsRecordSchema,
} from "@/features/dns/schemas/dns";

type ApiSuccess<T> = { success: true; data: T; message?: string };

export interface DnsRecordsQuery {
  type?: string;
  name?: string;
  content?: string;
  page?: number;
  per_page?: number;
}

type HttpQueryParams = Record<string, string | number | boolean | undefined>;

function toHttpQueryParams(query?: DnsRecordsQuery): HttpQueryParams | undefined {
  if (!query) return undefined;
  return {
    type: query.type,
    name: query.name,
    content: query.content,
    page: query.page,
    per_page: query.per_page,
  };
}

function assertListResult(data: unknown): IDnsRecordListResult {
  return dnsRecordListResultSchema.parse(data);
}

function assertRecord(data: unknown): IDnsRecord {
  return dnsRecordSchema.parse(data);
}

function assertZones(data: unknown): ICloudflareZone[] {
  const asArray = z.array(cloudflareZoneSchema).safeParse(data);
  if (asArray.success) return asArray.data;
  const wrapped = z.object({ zones: z.array(cloudflareZoneSchema) }).safeParse(data);
  if (wrapped.success) return wrapped.data.zones;
  return z.array(cloudflareZoneSchema).parse(data);
}

export async function fetchConfiguredDnsZone(): Promise<{ id: string; name?: string }> {
  const res = await http.get<ApiSuccess<unknown>>("/api/v1/dns/zone");
  return dnsConfiguredZoneSchema.parse(res.data);
}

export async function fetchDnsZones(): Promise<ICloudflareZone[]> {
  const res = await http.get<ApiSuccess<unknown>>("/api/v1/dns/zones");
  return assertZones(res.data);
}

export async function fetchDnsRecords(
  zoneId: string,
  query?: DnsRecordsQuery
): Promise<IDnsRecordListResult> {
  const res = await http.get<ApiSuccess<unknown>>(
    `/api/v1/dns/zones/${encodeURIComponent(zoneId)}/records`,
    toHttpQueryParams(query)
  );
  return assertListResult(res.data);
}

/** Lista registros de la zona configurada en el servidor (sin zoneId en la ruta). */
export async function fetchDnsRecordsDefaultZone(
  query?: DnsRecordsQuery
): Promise<IDnsRecordListResult> {
  const res = await http.get<ApiSuccess<unknown>>(
    "/api/v1/dns/records",
    toHttpQueryParams(query)
  );
  return assertListResult(res.data);
}

export async function fetchDnsRecord(
  zoneId: string,
  recordId: string
): Promise<IDnsRecord> {
  const res = await http.get<ApiSuccess<unknown>>(
    `/api/v1/dns/zones/${encodeURIComponent(zoneId)}/records/${encodeURIComponent(recordId)}`
  );
  return assertRecord(res.data);
}

export async function createDnsRecord(
  zoneId: string | null,
  body: Record<string, unknown>
): Promise<IDnsRecord> {
  const path =
    zoneId === null
      ? "/api/v1/dns/records"
      : `/api/v1/dns/zones/${encodeURIComponent(zoneId)}/records`;
  const res = await http.post<ApiSuccess<unknown>>(path, body);
  return assertRecord(res.data);
}

export async function updateDnsRecord(
  zoneId: string | null,
  recordId: string,
  body: Record<string, unknown>
): Promise<IDnsRecord> {
  const path =
    zoneId === null
      ? `/api/v1/dns/records/${encodeURIComponent(recordId)}`
      : `/api/v1/dns/zones/${encodeURIComponent(zoneId)}/records/${encodeURIComponent(recordId)}`;
  const res = await http.patch<ApiSuccess<unknown>>(path, body);
  return assertRecord(res.data);
}

export async function deleteDnsRecord(
  zoneId: string | null,
  recordId: string
): Promise<void> {
  const path =
    zoneId === null
      ? `/api/v1/dns/records/${encodeURIComponent(recordId)}`
      : `/api/v1/dns/zones/${encodeURIComponent(zoneId)}/records/${encodeURIComponent(recordId)}`;
  await http.delete<ApiSuccess<unknown>>(path);
}
